import { useEffect, useState } from 'react';
import {
  BODY_TYPES_WITH_CRANE,
  BODY_TYPE_LABEL,
  ENTITY_TYPES,
  ENTITY_TYPE_HINT,
  ENTITY_TYPE_LABEL,
  cancelRate,
  COMPANY_ROLE_HINT,
  COMPANY_ROLE_LABEL,
  manages as rolesManage,
  topRole,
  VEHICLE_KINDS,
  VEHICLE_KIND_LABEL,
  type BodyType,
  type EntityType,
  type MyCompanyRow,
  type VehicleKind,
  isCurrency,
} from 'shared';
import { useFormErrors } from '../useFormErrors';
import FieldError from '../FieldError';
import { api, type Me } from '../api';
import { useMoney } from '../money';
import Hint from '../Hint';
import BodyTypeSelect from '../BodyTypeSelect';
import DriverDocs from '../DriverDocs';
import RegionPicker from '../RegionPicker';
import { alert, haptic, isTelegram, notify, openTelegram } from '../telegram';
import { resetTour } from '../tourState';

export default function Profile() {
  const [me, setMe] = useState<Me | null>(null);
  const [inn, setInn] = useState('');
  const [busy, setBusy] = useState(false);

  /*
   * Свой набор ошибок на каждую форму профиля.
   *
   * Один общий подсветил бы поле ИНН при промахе в пароле: имена полей
   * там разные, но прокрутка уводила бы к чужой карточке.
   */
  const pwForm = useFormErrors();
  const credForm = useFormErrors();
  const innForm = useFormErrors();
  const carForm = useFormErrors();
  const linkForm = useFormErrors();
  const [linkCode, setLinkCode] = useState('');

  // Новая машина
  const [plate, setPlate] = useState('');
  /** Вид техники: машина, прицеп, полуприцеп или платформа. */
  const [vKind, setVKind] = useState<VehicleKind>('TRUCK');
  /** Записать на компанию, а не на себя. Пусто — техника частника. */
  const [vCompanyId, setVCompanyId] = useState('');
  /** Компании, которыми человек распоряжается: на них можно записать технику. */
  const [companies, setCompanies] = useState<MyCompanyRow[]>([]);
  /** Компании, которыми человек распоряжается: на них можно записать технику. */
  const managed = companies.filter(
    (c) => rolesManage(c.roles),
  );
  const [bodyType, setBodyType] = useState<BodyType>('TENT');
  const [capacityT, setCapacityT] = useState('20');
  const [vCraneT, setVCraneT] = useState('');
  const [vCraneReach, setVCraneReach] = useState('');

  // Новая подписка
  const [subFrom, setSubFrom] = useState('');
  const [subTo, setSubTo] = useState('');
  const [subMinPrice, setSubMinPrice] = useState('');

  // Привязка аккаунтов
  const [link, setLink] = useState<{ code: string; deepLink: string | null } | null>(null);
  const [showMerge, setShowMerge] = useState(false);
  const [mergeLogin, setMergeLogin] = useState('');
  const [mergePassword, setMergePassword] = useState('');

  // Логин и пароль
  const [newLogin, setNewLogin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [curPassword, setCurPassword] = useState('');

  async function load() {
    try {
      setMe(await api.me());
    } catch (e) {
      alert((e as Error).message);
    }
  }

  useEffect(() => {
    // Только управляющие роли: водитель технику на компанию не записывает.
    api
      .myCompanies()
      .then((r) => setCompanies(r.items))
      .catch(() => {});
  }, []);

  useEffect(() => {
    void load();
  }, []);

  async function requestLink() {
    setBusy(true);
    try {
      setLink(await api.linkTelegram());
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /** Влить существующий веб-аккаунт в текущий. */
  async function mergeExisting() {
    setBusy(true);
    try {
      await api.linkPassword(mergeLogin.trim().toLowerCase(), mergePassword);
      notify('success');
      setShowMerge(false);
      setMergeLogin('');
      setMergePassword('');
      alert('Аккаунты объединены. Заявки, рейсы и рейтинг теперь в одном профиле.');
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveCreds() {
    const bad: Record<string, string> = {};
    if (newLogin.trim().length < 3) bad.newLogin = 'Логин от 3 символов';
    if (newPassword.length < 8) bad.newPassword = 'Пароль от 8 символов';
    if (Object.keys(bad).length) return credForm.fail(bad);

    credForm.reset();
    setBusy(true);
    try {
      await api.setCredentials(newLogin.trim().toLowerCase(), newPassword);
      notify('success');
      setNewLogin('');
      setNewPassword('');
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function changePw() {
    const bad: Record<string, string> = {};
    if (!curPassword) bad.curPassword = 'Введите текущий пароль';
    if (newPassword.length < 8) bad.newPassword = 'Новый пароль от 8 символов';
    if (Object.keys(bad).length) return pwForm.fail(bad);

    pwForm.reset();
    setBusy(true);
    try {
      await api.changePassword(curPassword, newPassword);
      notify('success');
      setCurPassword('');
      setNewPassword('');
      alert('Пароль изменён.');
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const { options, setDisplay } = useMoney();
  const displayOptions = options.filter((o) => o.display);
  const [displayCurrency, setDisplayCurrency] = useState('');

  useEffect(() => {
    setDisplayCurrency(me?.displayCurrency ?? '');
  }, [me?.displayCurrency]);

  async function saveDisplayCurrency(value: string) {
    setDisplayCurrency(value);
    setBusy(true);
    try {
      await api.updateMe({ displayCurrency: value });
      // Обновляем и общий контекст: суммы на других экранах должны
      // перерисоваться сразу, а не после перезапуска приложения.
      setDisplay(isCurrency(value) ? value : null);
      notify('success');
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function setEntityType(entityType: EntityType) {
    setBusy(true);
    try {
      await api.updateMe({ entityType });
      notify('success');
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveInn() {
    if (!/^(\d{10}|\d{12})$/.test(inn)) {
      return innForm.fail({ inn: 'ИНН — 10 цифр у компании, 12 у ИП' });
    }

    innForm.reset();
    setBusy(true);
    try {
      await api.updateMe({ inn });
      notify('success');
      setInn('');
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addVehicle() {
    const bad: Record<string, string> = {};
    if (plate.length < 4) bad.plate = 'Укажите госномер';
    if (!Number(capacityT)) bad.capacityT = 'Укажите грузоподъёмность';
    if (Object.keys(bad).length) return carForm.fail(bad);

    carForm.reset();
    setBusy(true);
    try {
      await api.addVehicle({
        kind: vKind,
        plate,
        bodyType,
        // Техника, записанная на компанию, остаётся у неё, когда водитель
        // уходит: вместе с документами и историей рейсов.
        companyId: vCompanyId || null,
        capacityKg: Math.round(Number(capacityT) * 1000),
        craneCapacityKg: vCraneT ? Math.round(Number(vCraneT) * 1000) : null,
        craneReachM: vCraneReach ? Number(vCraneReach) : null,
      });
      notify('success');
      setPlate('');
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addSubscription() {
    if (!subFrom && !subTo) return alert('Выберите регион загрузки или выгрузки');
    setBusy(true);
    try {
      await api.addSubscription({
        fromRegion: subFrom || undefined,
        toRegion: subTo || undefined,
        bodyTypes: [],
        minPrice: subMinPrice ? Number(subMinPrice) : undefined,
      });
      notify('success');
      setSubFrom('');
      setSubTo('');
      setSubMinPrice('');
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeSubscription(id: string) {
    await api.deleteSubscription(id);
    await load();
  }

  if (!me) return <div className="empty">Загрузка…</div>;

  return (
    <>
      <h2>Профиль</h2>

      <div className="card">
        <strong>
          {[me.firstName, me.lastName].filter(Boolean).join(' ') || 'Без имени'}
        </strong>
        <div className="meta">
          {me.phone ?? 'Номер не указан'}
          {me.ratingCount > 0 && ` · ★ ${me.rating.toFixed(1)} (${me.ratingCount})`}
        </div>
        <div className="divider" />

        <div className="row">
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div className="price" style={{ fontSize: 17 }}>
              {me.cargosPublished}
            </div>
            <div className="meta">заявок</div>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div className="price" style={{ fontSize: 17 }}>
              {me.ordersDone}
            </div>
            <div className="meta">сделок</div>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div className="price" style={{ fontSize: 17 }}>
              {cancelRate(me.ordersDone, me.ordersCancelled) ?? '—'}
              {cancelRate(me.ordersDone, me.ordersCancelled) !== null && '%'}
            </div>
            <div className="meta">отмен</div>
          </div>
        </div>

        <div className="meta" style={{ marginTop: 12 }}>
          Это видят контрагенты до сделки. Активных рейсов сейчас:{' '}
          {me.stats.activeOrders}.
        </div>
      </div>

      {/* Обучение проходят один раз, но возвращаются к нему редко и в
          растерянности — поэтому кнопка лежит в профиле, а не в меню
          «Ещё». Перезагрузка, а не флаг через полдерева: обучение
          запускается на старте приложения, и начать его заново проще
          тем же путём, каким оно начинается само. */}
      <div className="card">
        <h3>Обучение</h3>
        <div className="meta" style={{ marginBottom: 12 }}>
          Короткий тур по разделам: где искать грузы, как откликаться и что
          происходит после того, как заявку взяли.
        </div>
        <button
          type="button"
          className="secondary btn--compact"
          onClick={() => {
            haptic();
            resetTour();
            window.location.reload();
          }}
        >
          Пройти заново
        </button>
      </div>

      {/* Валюта отображения — это вид, а не условие сделки. Формулировка
          здесь важнее вёрстки: человек не должен решить, что выбором в
          профиле он меняет сумму, которую ему заплатят. */}
      {displayOptions.length > 0 && (
        <div className="card">
          <h3>Валюта отображения</h3>
          <div className="meta" style={{ marginBottom: 12 }}>
            Только для показа. Сумма сделки остаётся в той валюте, в которой её
            назначили, и от этой настройки не меняется.
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <select
              value={displayCurrency}
              disabled={busy}
              onChange={(e) => void saveDisplayCurrency(e.target.value)}
            >
              <option value="">В валюте сделки</option>
              {displayOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.sign} {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="card">
        <h3>Вход в аккаунт</h3>

        <div className="meta" style={{ marginBottom: 12 }}>
          {me.hasPassword && me.hasTelegram
            ? 'Вход и по логину, и через Telegram ведут в этот профиль.'
            : me.hasTelegram
              ? 'Сейчас вход только через Telegram.'
              : 'Сейчас вход только по логину и паролю.'}
        </div>

        {!me.hasTelegram && (
          <>
            <div className="divider" />
            <h3>Привязать Telegram</h3>
            <div className="meta" style={{ marginBottom: 12 }}>
              Чтобы открывать тот же профиль из бота. Если в боте у вас уже есть
              отдельный аккаунт, он вольётся в этот.
            </div>

            {link ? (
              <>
                <div className="card card--inset">
                  <div className="meta">Код действует 15 минут</div>
                  <div className="price" style={{ letterSpacing: 2 }}>{link.code}</div>
                </div>
                {link.deepLink && (
                  <button onClick={() => openTelegram(link.deepLink!)}>
                    Открыть бота и привязать
                  </button>
                )}
                <div className="meta" style={{ marginTop: 8 }}>
                  Или отправьте боту команду <code>/link {link.code}</code>
                </div>
              </>
            ) : (
              <button className="secondary" disabled={busy} onClick={() => void requestLink()}>
                Получить код привязки
              </button>
            )}
          </>
        )}

        {/* Обратная сторона привязки: код из веб-кабинета вводится здесь.
            Нужен, когда сервер не достучится до Telegram — бот тогда команд
            не получает вовсе, а приложение работает. */}
        {me.hasTelegram && isTelegram && (
          <>
            <div className="divider" />
            <h3>Привязать веб-аккаунт</h3>
            <div className="meta" style={{ marginBottom: 12 }}>
              Возьмите код в веб-кабинете → Профиль → «Получить код привязки»
              и введите его здесь.
            </div>

            <div className="field">
              <input
                {...linkForm.field('linkCode')}
                value={linkCode}
                placeholder="ABC123"
                onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
              />
              <FieldError form={linkForm} name="linkCode" />
            </div>
            <button
              className="secondary"
              disabled={busy || linkCode.length < 4}
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await api.linkByCode(linkCode.trim());
                  notify('success');
                  await alert(
                    `Аккаунты объединены${r.login ? `: ${r.login}` : ''}. Войдите заново.`,
                  );
                  window.location.reload();
                } catch (e) {
                  // Причина отказа — про этот код, поэтому и показывается
                  // под полем, а не в окне, которое закрывают не читая.
                  linkForm.fail({ linkCode: (e as Error).message });
                } finally {
                  setBusy(false);
                }
              }}
            >
              Привязать по коду
            </button>
          </>
        )}

        {me.hasTelegram && !me.hasPassword && (
          <>
            <div className="divider" />
            {!showMerge ? (
              <button className="secondary" onClick={() => setShowMerge(true)}>
                У меня уже есть аккаунт с логином
              </button>
            ) : (
              <>
                <h3>Объединить с существующим аккаунтом</h3>
                <div className="meta" style={{ marginBottom: 12 }}>
                  Введите логин и пароль от аккаунта в вебе — заявки, рейсы
                  и рейтинг переедут сюда, а тот аккаунт закроется.
                </div>
                <div className="field">
                  <label>Логин</label>
                  <input
                    autoCapitalize="none"
                    value={mergeLogin}
                    onChange={(e) => setMergeLogin(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>Пароль</label>
                  <input
                    type="password"
                    value={mergePassword}
                    onChange={(e) => setMergePassword(e.target.value)}
                  />
                </div>
                <div className="col">
                  <button disabled={busy} onClick={() => void mergeExisting()}>
                    Объединить
                  </button>
                  <button className="secondary" onClick={() => setShowMerge(false)}>
                    Отмена
                  </button>
                </div>
              </>
            )}
          </>
        )}

        <div className="divider" />

        {me.hasPassword ? (
          <>
            <div className="meta">
              Логин <strong>{me.login}</strong>
            </div>

            <div className="divider" />

            <div className="field">
              <label>Текущий пароль</label>
              <input
                {...pwForm.field('curPassword')}
                type="password"
                autoComplete="current-password"
                value={curPassword}
                onChange={(e) => setCurPassword(e.target.value)}
              />
              <FieldError form={pwForm} name="curPassword" />
            </div>
            <div className="field">
              <label>Новый пароль</label>
              <input
                {...pwForm.field('newPassword')}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <FieldError form={pwForm} name="newPassword" />
            </div>
            <button className="secondary" disabled={busy} onClick={() => void changePw()}>
              Сменить пароль
            </button>
          </>
        ) : (
          <>
            <div className="meta" style={{ marginBottom: 12 }}>
              Задайте логин и пароль, чтобы заходить с компьютера через браузер —
              в вебе работать с заявками удобнее, чем в окне Telegram.
            </div>
            <div className="field">
              <label>Логин</label>
              <input
                {...credForm.field('newLogin')}
                autoCapitalize="none"
                placeholder="ivanov"
                value={newLogin}
                onChange={(e) => setNewLogin(e.target.value)}
              />
              <FieldError form={credForm} name="newLogin" />
            </div>
            <div className="field">
              <label>Пароль</label>
              <input
                {...credForm.field('newPassword')}
                type="password"
                autoComplete="new-password"
                placeholder="от 8 символов"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <FieldError form={credForm} name="newPassword" />
            </div>
            <button className="secondary" disabled={busy} onClick={() => void saveCreds()}>
              Сохранить
            </button>
          </>
        )}
      </div>

      <div className="card">
        <h3>
          Статус <Hint k="entityType" />
        </h3>
        <div className="meta" style={{ marginBottom: 12 }}>
          Начать можно физлицом. Статус меняется в любой момент — рейтинг и история
          сделок сохраняются.
        </div>

        <div className="chips">
          {ENTITY_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={`chip ${me.entityType === t ? 'on' : ''}`}
              disabled={busy}
              onClick={() => void setEntityType(t)}
            >
              {ENTITY_TYPE_LABEL[t]}
            </button>
          ))}
        </div>

        <div className="meta" style={{ marginTop: 12 }}>
          {ENTITY_TYPE_HINT[me.entityType]}
        </div>

        {me.entityType === 'INDIVIDUAL' && (
          <div className="meta" style={{ marginTop: 12 }}>
            Заявки с безналичной оплатой вам недоступны — заказчик-юрлицо не сможет
            легально провести платёж. Статус самозанятого оформляется в приложении
            «Мой налог» за несколько минут и открывает такие заявки.
          </div>
        )}
      </div>

      <div className="card">
        <h3>Компания</h3>
        {me.company ? (
          <>
            <div>{me.company.name}</div>
            <div className="meta">ИНН {me.company.inn}</div>
            <div style={{ marginTop: 8 }}>
              {me.company.verified ? (
                <span className="badge badge--verified">проверена</span>
              ) : (
                <span className="badge">на модерации</span>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="meta" style={{ marginBottom: 12 }}>
              Без ИНН заявки видны, но отклики от вас будут в самом низу списка.
            </div>
            <div className="field">
              <input
                {...innForm.field('inn')}
                inputMode="numeric"
                placeholder="ИНН"
                value={inn}
                onChange={(e) => setInn(e.target.value)}
              />
              <FieldError form={innForm} name="inn" />
            </div>
            <button disabled={busy} onClick={() => void saveInn()}>
              Привязать компанию
            </button>
          </>
        )}
      </div>

      {/*
        * Место в компании.
        *
        * Наёмный водитель заходит в профиль и не понимает, почему у него нет
        * половины разделов и чья на нём машина. Ответ — здесь: он сотрудник,
        * и часть решений принимает не он.
        */}
      {companies.length > 0 && (
        <div className="card">
          <h3>Где вы работаете</h3>
          {companies.map((c) => (
            <div key={c.id} className="doc-row">
              <span>
                <strong>{c.name}</strong>
                <div className="meta">
                  {c.roles.map((r) => COMPANY_ROLE_LABEL[r]).join(', ')}
                  {c.verified ? ' · компания проверена' : ''}
                </div>
              </span>
              <span className="meta" style={{ textAlign: 'right' }}>
                {c.canSeeRates ? 'видит суммы' : 'суммы скрыты'}
                <br />
                {c.canBook ? 'берёт грузы' : 'грузы берёт диспетчер'}
              </span>
            </div>
          ))}
          <div className="meta" style={{ marginTop: 8 }}>
            {COMPANY_ROLE_HINT[topRole(companies[0]!.roles)]}
          </div>
        </div>
      )}

      <div className="card">
        <h3>{managed.length > 0 ? 'Техника' : 'Мои машины'}</h3>
        {/* Техника компании живёт в её парке: здесь только то, что записано
            на человека. Иначе водитель видит чужие машины в личном списке
            и пытается ими распоряжаться. */}
        {managed.length > 0 && (
          <div className="meta" style={{ marginBottom: 8 }}>
            Машины компании — во вкладке «Компания → Парк». Здесь только ваши
            личные.
          </div>
        )}
        {me.vehicles.map((v) => (
          <div key={v.id} className="meta">
            {VEHICLE_KIND_LABEL[(v.kind ?? 'TRUCK') as VehicleKind]} {v.plate} ·{' '}
            {BODY_TYPE_LABEL[v.bodyType as BodyType]} ·{' '}
            {(v.capacityKg / 1000).toFixed(1)} т
            {v.craneCapacityKg
              ? ` · кран ${(v.craneCapacityKg / 1000).toFixed(1)} т`
              : ''}
            {v.craneReachM ? ` · вылет ${v.craneReachM} м` : ''}
          </div>
        ))}
        {me.vehicles.length === 0 && <div className="meta">Пока не добавлены.</div>}

        <div className="divider" />

        <div className="field-row">
          <div className="field">
            <label>Что заводим</label>
            <select
              value={vKind}
              onChange={(e) => setVKind(e.target.value as VehicleKind)}
            >
              {VEHICLE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {VEHICLE_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
          {/* Записать на компанию может тот, кто ею распоряжается. Список
              приходит из «моих компаний» — чужую подставить нельзя. */}
          {managed.length > 0 && (
            <div className="field">
              <label>Чья техника</label>
              <select
                value={vCompanyId}
                onChange={(e) => setVCompanyId(e.target.value)}
              >
                <option value="">Моя</option>
                {managed.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="field-row">
          <div className="field">
            <input
              {...carForm.field('plate')}
              placeholder="А123ВС777"
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
            />
            <FieldError form={carForm} name="plate" />
          </div>
          <div className="field">
            <input
              {...carForm.field('capacityT')}
              type="number"
              inputMode="decimal"
              placeholder="тонн"
              value={capacityT}
              onChange={(e) => setCapacityT(e.target.value)}
            />
            <FieldError form={carForm} name="capacityT" />
          </div>
        </div>
        <div className="field">
          <BodyTypeSelect value={bodyType} onChange={(v) => v && setBodyType(v)} />
        </div>
        {BODY_TYPES_WITH_CRANE.includes(bodyType) && (
          <div className="field-row">
            <div className="field">
              <label>Кран поднимает, т</label>
              <input
                type="text"
                inputMode="decimal"
                value={vCraneT}
                onChange={(e) => setVCraneT(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Вылет стрелы, м</label>
              <input
                type="text"
                inputMode="decimal"
                value={vCraneReach}
                onChange={(e) => setVCraneReach(e.target.value)}
              />
            </div>
          </div>
        )}

        <button className="secondary" disabled={busy} onClick={() => void addVehicle()}>
          Добавить машину
        </button>
      </div>

      <DriverDocs />

      <div className="card">
        <h3>Подписки на направления</h3>
        <div className="meta" style={{ marginBottom: 12 }}>
          Новые грузы по этим направлениям бот пришлёт в чат.
        </div>

        {me.subscriptions.map((s) => (
          <div key={s.id} className="row" style={{ marginBottom: 8 }}>
            <div className="meta">
              {s.fromRegion ?? 'любой'} → {s.toRegion ?? 'любой'}
              {s.minPrice ? ` · от ${s.minPrice} ₽` : ''}
            </div>
            <button
              className="danger small"
              onClick={() => void removeSubscription(s.id)}
            >
              удалить
            </button>
          </div>
        ))}
        {me.subscriptions.length === 0 && <div className="meta">Пока нет.</div>}

        <div className="divider" />

        <div className="field-row">
          <div className="field">
            <label>Откуда</label>
            <RegionPicker label="Регион откуда" value={subFrom} onChange={setSubFrom} allowEmpty="Любой" />
          </div>
          <div className="field">
            <label>Куда</label>
            <RegionPicker label="Регион куда" value={subTo} onChange={setSubTo} allowEmpty="Любой" />
          </div>
        </div>
        <div className="field">
          <label>Ставка от, ₽</label>
          <input
            type="number"
            inputMode="numeric"
            placeholder="не важно"
            value={subMinPrice}
            onChange={(e) => setSubMinPrice(e.target.value)}
          />
        </div>
        <button className="secondary" disabled={busy} onClick={() => void addSubscription()}>
          Подписаться
        </button>
      </div>
    </>
  );
}
